/* Shared production layout for /course-catalog/$slug/ (approved "version B" design). */
import { Link } from "@tanstack/react-router";
import { Check, ChevronRight, Clock, CalendarRange, Layers, MessageSquare, ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import { UpcomingSessions } from "@/components/site/UpcomingSessions";
import { VideoPlaceholder } from "@/components/site/VideoPlaceholder";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";
import type { Course } from "@/content/courses";
import boardroom from "@/assets/boardroom.png";

/** Per-course wide hero photos: src/assets/course-heroes/<slug>.webp. No file = no image block. */
const HERO_IMAGES: Record<string, string> = Object.fromEntries(
  Object.entries(
    import.meta.glob<string>("../../assets/course-heroes/*.webp", { eager: true, import: "default" }),
  ).map(([p, url]) => [p.split("/").pop()!.replace(/\.webp$/, ""), url]),
);

const SUPPORT_EMAIL = "support@leadershipedge.live";

const H2 = "text-3xl font-semibold leading-tight text-ink md:text-4xl";
const BTN =
  "inline-flex min-h-11 items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand transition-transform hover:-translate-y-0.5";
const SOFT_CARD =
  "rounded-2xl border border-[#693D94]/10 bg-background p-6 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_32px_-16px_rgba(38,24,58,0.25)]";
const TRI =
  "M200 46c14 0 26 8 33 20l104 180c7 12 7 26 0 38s-19 19-33 19H96c-14 0-26-7-33-19s-7-26 0-38L167 66c7-12 19-20 33-20z";

function Rule() {
  return <span aria-hidden="true" className="mt-4 block h-1 w-12 rounded-full bg-[#693D94]" />;
}

function QuestionsCard() {
  return (
    <div className={SOFT_CARD}>
      <div className="flex items-start gap-4">
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-lavender">
          <MessageSquare className="h-5 w-5 text-[#693D94]" strokeWidth={1.5} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-ink">Questions?</h3>
          <p className="mt-1 text-base leading-relaxed text-ink/80">Email us and a real person will reply.</p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="mt-2 flex min-h-11 w-full min-w-0 items-center text-[15px] font-semibold text-[#693D94] hover:underline"
          >
            <span className="min-w-0 [overflow-wrap:anywhere] xl:whitespace-nowrap">{SUPPORT_EMAIL}</span>
          </a>
        </div>
      </div>
    </div>
  );
}

export function CoursePageLayout({ course }: { course: Course }) {
  const heroImage = HERO_IMAGES[course.slug];
  const sessions = course.sessions ?? [];
  const outcomes = course.outcomes ?? [];
  const behaviors = course.behaviors ?? [];

  const facts: { icon: ReactNode; label: string; value: string }[] = [];
  const ic = "h-5 w-5 text-[#693D94]";
  if (course.duration) facts.push({ icon: <CalendarRange className={ic} strokeWidth={1.5} aria-hidden="true" />, label: "Duration", value: course.duration });
  if (sessions.length) facts.push({ icon: <Layers className={ic} strokeWidth={1.5} aria-hidden="true" />, label: "Sessions", value: String(sessions.length) });
  if (course.weeklyTime) facts.push({ icon: <Clock className={ic} strokeWidth={1.5} aria-hidden="true" />, label: "Time per week", value: course.weeklyTime });

  const scrollToSchedule = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById("upcoming-schedule")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div style={{ fontFamily: "'Poppins', sans-serif" }} className="overflow-x-clip">
      {/* 1. HERO BAND */}
      <section className="relative bg-gradient-to-b from-lavender to-background">
        <svg aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 1440 600" preserveAspectRatio="xMidYMid slice" fill="none">
          <path d={TRI} fill="#693D94" fillOpacity={0.06} transform="translate(1040 -60) rotate(-16 200 200) scale(1.5)" />
          <path d={TRI} fill="#693D94" fillOpacity={0.04} transform="translate(-120 220) rotate(12 200 200) scale(1.1)" />
          <path d={TRI} fill="#693D94" fillOpacity={0.08} transform="translate(900 300) rotate(20 200 200) scale(0.45)" />
        </svg>
        <div className={`relative mx-auto max-w-6xl px-6 pt-14 pb-32 md:pt-20 lg:pt-4 ${heroImage ? "md:pb-64 lg:pb-[236px]" : ""}`}>
          <nav aria-label="Breadcrumb" className="mb-6 lg:mb-2">
            <ol className="flex flex-wrap items-center gap-1 text-sm font-semibold text-ink/75">
              <li><Link to="/course-catalog/" className="inline-flex min-h-11 items-center text-[#693D94] hover:underline lg:min-h-9">Courses</Link></li>
              <li aria-hidden="true"><ChevronRight className="h-4 w-4" /></li>
              <li>{course.title}</li>
            </ol>
          </nav>
          <h1 className="max-w-4xl break-words text-4xl font-bold leading-[1.08] tracking-tight text-ink md:text-6xl lg:text-[48px]">
            {course.title}
          </h1>
          {course.tagline && (
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink/80">{course.tagline}</p>
          )}
        </div>
      </section>

      {/* 2. WIDE IMAGE straddling the hero edge + floating facts */}
      <div className={`relative mx-auto -mt-12 max-w-6xl px-6 ${heroImage ? "md:-mt-44 lg:-mt-[212px]" : ""}`}>
        <div className="relative">
          {heroImage && (
          <>
          {/* Image only from md up; phones get a 1x1 transparent fallback and never fetch the file. */}
          <div className="hidden overflow-hidden rounded-2xl shadow-[0_2px_6px_rgba(16,24,40,0.06),0_30px_60px_-24px_rgba(38,24,58,0.45)] md:block">
            <picture>
              <source media="(min-width: 768px)" srcSet={heroImage} />
              <img src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7" alt={`${course.title} course`} width={960} height={453} className={`aspect-[16/10] w-full object-cover lg:aspect-[26/10]`} />
            </picture>
          </div>
          </>
          )}
          {facts.length > 0 && (
            <dl className={`grid grid-cols-2 gap-x-6 gap-y-5 rounded-2xl bg-background p-5 shadow-[0_12px_32px_-16px_rgba(38,24,58,0.3)] lg:grid-cols-3 lg:p-6 ${heroImage ? `mt-0 md:mt-6 lg:absolute lg:inset-x-10 lg:-bottom-12 lg:mt-0` : ""}`}>
              {facts.map((f) => (
                <div key={f.label} className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-lavender">{f.icon}</span>
                  <div className="min-w-0">
                    <dt className="text-sm font-medium text-ink/75">{f.label}</dt>
                    <dd className="text-base font-semibold text-ink">{f.value}</dd>
                  </div>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>

      {/* 3. WHO / OVERVIEW */}
      {(course.whoFor || course.overview) && (
        <section className="bg-background">
          <div className={`mx-auto grid max-w-6xl gap-12 px-6 pt-16 pb-16 md:pt-24 lg:grid-cols-2 lg:gap-16 lg:pt-32`}>
            {course.whoFor && (
              <div>
                <h2 className={H2}>Who is the course for?</h2>
                <Rule />
                <p className="mt-6 text-lg leading-8 text-ink/85">{course.whoFor}</p>
              </div>
            )}
            {course.overview && (
              <div>
                <h2 className={H2}>Course description</h2>
                <Rule />
                <p className="mt-6 text-base leading-8 text-ink/80 first-line:text-lg first-line:font-medium first-line:text-ink">
                  {course.overview}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 4. MAIN GRID + sticky sidebar */}
      <section className="bg-gradient-to-b from-background via-lavender/40 to-background">
        <div className="mx-auto grid max-w-6xl gap-16 px-6 py-16 md:py-20 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:gap-14">
          <div className="min-w-0 space-y-20">
            {outcomes.length > 0 && (
              <div>
                <h2 className={H2}>Completing this course will help you</h2>
                <ul className="mt-8">
                  {outcomes.map((o) => (
                    <li key={o} className="flex items-start gap-4 border-b border-[#693D94]/15 py-5 last:border-b-0">
                      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#693D94]">
                        <Check className="h-4 w-4 text-primary-foreground" strokeWidth={2.25} aria-hidden="true" />
                      </span>
                      <p className="text-[17px] leading-relaxed text-ink/85">{o}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {sessions.length > 0 && (
              <div>
                <h2 className={H2}>{sessions.length} {sessions.length === 1 ? "Module" : "Modules"}</h2>
                <ol className="relative mt-10">
                  <span aria-hidden="true" className="absolute top-2 bottom-2 left-[21px] w-px bg-[#693D94]/30" />
                  {sessions.map((s, i) => (
                    <li key={s.title} className="group relative flex gap-6 rounded-2xl py-4 pr-4 transition-colors hover:bg-lavender/60">
                      <span className="relative z-10 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-[#693D94] bg-background text-sm font-semibold text-[#693D94] transition-colors group-hover:bg-[#693D94] group-hover:text-primary-foreground">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0 pt-2">
                        <h3 className="text-xl font-semibold text-ink">{s.title}</h3>
                        <p className="mt-2 text-base leading-relaxed text-ink/80">{s.body}</p>
                        {s.format && <p className="mt-3 text-sm font-medium text-ink/75">{s.format}</p>}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {behaviors.length > 0 && (
              <div>
                <h2 className={H2}>Leader Behaviors</h2>
                <div className="mt-8 flex flex-wrap gap-2.5 rounded-2xl bg-lavender/70 p-6 md:p-8">
                  {behaviors.map((b) => (
                    <span key={b} className="inline-flex items-center rounded-full border border-[#693D94]/20 bg-background px-4 py-2 text-base font-medium text-[#693D94] shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
                      {b}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {!course.detailed && (
              <div className={SOFT_CARD}>
                <p className="text-base leading-relaxed text-ink/80">
                  Full course details are on their way. Ask us for the outline and we'll send it over.
                </p>
                <Link to="/contact/" className="mt-3 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-[#693D94] hover:underline">
                  Ask for the outline <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}

            <div id="upcoming-schedule" className="scroll-mt-28">
              <UpcomingSessions courseSlug={course.slug} heading="Upcoming Schedule" />
            </div>

            <div className="space-y-5 lg:hidden">
              <div data-mobile-cta className={SOFT_CARD}>
                <h3 className="text-xl font-semibold leading-snug text-ink">Ready to bring this to your team?</h3>
                <Link to="/contact/" className={`${BTN} mt-5 w-full`}>Talk to a Development Pro</Link>
              </div>
              <QuestionsCard />
            </div>
          </div>

          <aside className="hidden lg:sticky lg:top-28 lg:block lg:self-start">
            <div className="space-y-5">
              <div className={SOFT_CARD}>
                <h3 className="text-xl font-semibold leading-snug text-ink">Ready to bring this to your team?</h3>
                <Link to="/contact/" className={`${BTN} mt-5 w-full`}>Talk to a Development Pro</Link>
                <a href="#upcoming-schedule" onClick={scrollToSchedule} className="mt-3 inline-flex min-h-11 w-full items-center justify-center gap-1.5 text-sm font-semibold text-[#693D94] hover:underline">
                  See upcoming schedule <ArrowRight className="h-4 w-4" />
                </a>
              </div>
              <QuestionsCard />
            </div>
          </aside>
        </div>
      </section>

      {/* 5. VIDEO BAND: only for courses with a real video */}
      {course.video && (
        <section className="bg-lavender/60">
          <div className="mx-auto max-w-5xl px-6 py-20 md:py-24">
            <div className="rounded-2xl shadow-[0_30px_60px_-24px_rgba(38,24,58,0.35)]">
              <VideoPlaceholder src={course.video} poster={course.image} label={`${course.title} video`} />
            </div>
          </div>
        </section>
      )}

      {/* 6. CLOSING CTA */}
      <AdvisorCTA
        title="Not sure where to start?"
        body="A Development Pro can match courses to the roles and behaviors worth developing first."
        backgroundImage={boardroom}
        disableAnimation
      />
    </div>
  );
}
